import { getReferencePairs } from '@/utils/forexRates';

const EURUSD_DIGITS = 4;
const EURUSD_PIP_SIZE = 0.0001;

const referencePairs = getReferencePairs();

function CurrencyNode({ code, role, tone }: { code: string; role: string; tone: 'primary' | 'accent' }) {
  const toneClass =
    tone === 'primary'
      ? 'border-primary-200 bg-primary-50 text-primary-700'
      : 'border-accent-200 bg-accent-50 text-accent-700';
  return (
    <div className="flex flex-col items-center gap-2">
      <span
        className={`flex h-16 w-16 items-center justify-center rounded-full border font-heading text-lg font-semibold ${toneClass}`}
      >
        {code}
      </span>
      <span className="text-xs font-medium text-foreground-600">{role}</span>
    </div>
  );
}

export default function RateReferenceCard() {
  const pair = referencePairs.find((item) => item.symbol === 'EUR/USD');

  const price = pair ? Number(pair.price) : Number.NaN;
  const spreadPips = pair ? Number(pair.spread) : Number.NaN;
  const halfSpread = (spreadPips * EURUSD_PIP_SIZE) / 2;

  const ask = Number.isFinite(price) ? (price + halfSpread).toFixed(EURUSD_DIGITS) : '—';
  const bid = Number.isFinite(price) ? (price - halfSpread).toFixed(EURUSD_DIGITS) : '—';
  const spreadLabel = Number.isFinite(spreadPips) ? `${spreadPips.toFixed(1)} pips` : '—';

  const rateRows = [
    { label: 'ราคาซื้อ (Ask)', value: ask, tone: 'text-primary-700' },
    { label: 'ราคาขาย (Bid)', value: bid, tone: 'text-accent-700' },
    { label: 'ส่วนต่าง (Spread)', value: spreadLabel, tone: 'text-secondary-700' },
  ];

  return (
    <div className="relative rounded-2xl border border-background-200 bg-background-50 p-6 md:p-7">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm font-semibold text-foreground-800">
          <i className="ri-node-tree text-base text-primary-600" />
          ความสัมพันธ์ของคู่เงิน
        </span>
        <span className="rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-medium text-accent-800">
          ตัวอย่างเพื่อการสอน
        </span>
      </div>

      <div className="mt-7 flex items-center gap-3">
        <CurrencyNode code="EUR" role="สกุลเงินฐาน" tone="primary" />

        <div className="flex flex-1 flex-col items-center gap-2">
          <span className="rounded-full bg-background-100 px-3 py-1 font-num text-xs font-semibold text-foreground-800">
            EUR / USD
          </span>
          <div className="relative h-px w-full bg-background-300">
            <span className="absolute -left-0.5 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 border-b border-l border-primary-500" />
            <span className="absolute -right-0.5 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 border-r border-t border-accent-500" />
          </div>
        </div>

        <CurrencyNode code="USD" role="สกุลเงินอ้างอิง" tone="accent" />
      </div>

      <div className="mt-7 rounded-xl border border-background-200 bg-background-100/50 p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <p className="flex items-center gap-2 text-xs font-medium text-foreground-600">
            <i className="ri-bar-chart-grouped-line text-sm text-primary-600" />
            ตัวอย่างราคาอ้างอิง
          </p>
          <span className="rounded-full bg-background-200/70 px-2 py-0.5 text-[10px] font-medium text-foreground-600">
            ค่าคงที่
          </span>
        </div>
        <ul className="flex flex-col divide-y divide-background-200">
          {rateRows.map((row) => (
            <li key={row.label} className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0">
              <span className="text-sm text-foreground-700">{row.label}</span>
              <span className={`font-num text-sm font-semibold ${row.tone}`}>{row.value}</span>
            </li>
          ))}
        </ul>
        <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-lock-line text-xs" />
            ตัวเลขในหน้านี้ไม่เชื่อมต่อตลาดและไม่มีการรีเฟรช
          </span>
        </p>
      </div>

      <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
        <i className="ri-information-line mt-0.5 text-sm" />
        ตัวเลขด้านบนเป็นตัวอย่างเพื่ออธิบายแนวคิด เรื่อง Ask / Bid / Spread เท่านั้น ไม่ใช่ราคาตลาดจริง
        และไม่ควรใช้ประกอบการตัดสินใจเทรด
      </p>
    </div>
  );
}